import React from "react";

/**
 * Terry, the mascot. He is decoration around content that already says what it
 * needs to, so every appearance is hidden from assistive tech and can't be
 * clicked — he must never sit between a parent and a button.
 *
 * Poses, and where the design boards put each one:
 *   curious  — magnifying glass, on the photo upload steps
 *   flying   — paper plane, crossing the landing hero
 *   heart    — hugging a heart, on payment
 *   hero     — cheering with a star, on order confirmed
 *   point    — pointing at the four steps on the landing page
 *   reading  — the finished book, on the pages & price step
 *   run      — fetching the story, crossing the generation loader
 *   sleeping — asleep on a stack of books, in the night-time reader
 *   thinking — wondering which world to pick, on the theme step
 *   walk     — strolling the bookshelf, in the library
 *   wave     — hello, on sign in
 */

const ANIM = {
    idle: "anim-terry-idle",
    run: "anim-terry-run",
    snooze: "anim-terry-snooze",
    none: "",
};

export default function Terry({ pose, anim = "idle", className = "", style }) {
    return (
        <img
            src={`/terry/terry-${pose}.png`}
            alt=""
            aria-hidden
            draggable={false}
            className={`pointer-events-none select-none drop-shadow-[0_14px_22px_rgba(45,27,78,0.18)] ${ANIM[anim] ?? ""} ${className}`}
            style={style}
        />
    );
}

/**
 * Terry standing in the corner of the meadow. Fixed, because the hills he is
 * standing on are fixed too (see .magic-bg::after) — pinned to the document he
 * would drift off the grass as you scroll. Hidden below `lg`, where the
 * content already fills the width and he would just crowd it.
 */
export function TerryCorner({ pose, className = "", style, side = "right" }) {
    return (
        <div
            className={`pointer-events-none fixed bottom-0 z-20 hidden sm:block ${
                side === "left" ? "left-1 lg:left-3" : "right-1 lg:right-3"
            } ${className}`}
            style={style}
        >
            <Terry pose={pose} className="h-[92px] lg:h-[132px] w-auto max-w-none" />
        </div>
    );
}

/**
 * Terry crossing the width of the nearest positioned ancestor — the paper
 * plane over the hero, the dash across the loading screen. Animating `left`
 * rather than a transform keeps the travel relative to the container instead
 * of to Terry's own width, so it works at any viewport size.
 */
export function TerryCrossing({ pose, anim = "none", duration = "18s", height = 86, className = "", style }) {
    return (
        <div
            className={`pointer-events-none absolute z-10 anim-terry-cross ${className}`}
            style={{ animationDuration: duration, ...style }}
        >
            <Terry pose={pose} anim={anim} style={{ height, width: "auto" }} />
        </div>
    );
}

/** Terry asleep, with the two `z`s drifting up off him. */
export function TerrySleeping({ className = "" }) {
    return (
        <div className={`pointer-events-none fixed bottom-2 right-2 lg:right-4 z-20 hidden sm:block w-[104px] lg:w-[150px] ${className}`} aria-hidden>
            <Terry pose="sleeping" anim="snooze" className="w-full h-auto max-w-none" />
            <span className="absolute -top-1 right-5 font-display text-lg text-lavender anim-zzz">z</span>
            <span className="absolute -top-1 right-9 font-display text-[13px] text-lavender anim-zzz" style={{ animationDelay: "1.2s" }}>z</span>
        </div>
    );
}
